import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ClipboardIcon,
  CheckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import DataGrid from "../../components/Datagrid";

export default function TicketingListPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Event?showAll=true`
        );
        const data = await response.json();
        setEvents(Array.isArray(data) ? data : []);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const handleCopy = (eventId) => {
    const url = `${window.location.origin}/event/${eventId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(eventId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleDelete = async (event) => {
    const confirmed = window.confirm(
      `Voulez-vous supprimer l'événement "${event.name}" ?`
    );
    if (!confirmed) return;

    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Event/remove`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
      }
    );

    if (response.ok) {
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
      return;
    }

    alert("Une erreur est survenue lors de la suppression.");
  };

  const rows = useMemo(() => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    return (events || []).map((event) => {
      const isPast = new Date(event.eventDate) < today;
      const locationText = event?.location?.formattedAddress
        ? event.location.formattedAddress
        : event?.location?.latitude != null &&
          event?.location?.longitude != null
        ? `${Number(event.location.latitude).toFixed(4)}, ${Number(
            event.location.longitude
          ).toFixed(4)}`
        : "—";

      return {
        id: event.id,
        eventName: event.name,
        date: formatDate(event.eventDate),
        location: locationText,
        ticketSold: `${event.ticketsSold} / ${event.maxTickets}`,
        isPast,
        raw: event,
      };
    });
  }, [events]);

  const columns = useMemo(
    () => [
      {
        key: "eventName",
        label: t("ticketing.eventNameLabel"),
        width: 260,
        cellClassName: "align-top",
        render: (row) => (
          <div className={`px-4 py-4 ${row.isPast ? "text-gray-400" : ""}`}>
            {row.eventName}
          </div>
        ),
      },
      {
        key: "date",
        label: t("Date"),
        width: 220,
        cellClassName: "align-top",
        render: (row) => (
          <div className={`px-4 py-4 ${row.isPast ? "text-gray-400" : ""}`}>
            {row.date}
          </div>
        ),
      },
      {
        key: "location",
        label: t("ticketing.locationLabel"),
        width: 420,
        cellClassName: "align-top",
        render: (row) => (
          <div className={`px-4 py-4 ${row.isPast ? "text-gray-400" : ""}`}>
            {row.location}
          </div>
        ),
      },
      {
        key: "ticketSold",
        label: t("ticketing.ticketSold"),
        width: 180,
        cellClassName: "align-top",
        render: (row) => (
          <div className={`px-4 py-4 ${row.isPast ? "text-gray-400" : ""}`}>
            {row.ticketSold}
          </div>
        ),
      },
      {
        key: "url",
        label: t("URL"),
        width: 120,
        cellClassName: "align-top",
        render: (row) => (
          <div className={`px-4 py-3 ${row.isPast ? "opacity-50" : ""}`}>
            <button
              type="button"
              className={`btn btn-sm transition-transform ${
                row.isPast ? "hidden cursor-not-allowed" : "btn-ghost"
              }`}
              disabled={row.isPast}
              onClick={() => handleCopy(row.id)}
            >
              <AnimatePresence mode="wait" initial={false}>
                {copiedId === row.id ? (
                  <motion.span
                    key="check"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CheckIcon className="w-5 h-5 text-green-500" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="clipboard"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 2 }}
                    transition={{ duration: 0.4 }}
                  >
                    <ClipboardIcon className="w-5 h-5 text-gray-700" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        ),
      },
      {
        key: "delete",
        label: t("Supprimer"),
        width: 140,
        cellClassName: "align-top",
        render: (row) => (
          <div className="px-4 py-3">
            <button
              type="button"
              onClick={() => handleDelete(row.raw)}
              className="btn btn-sm"
            >
              <TrashIcon className="w-5 h-5 text-red-600" />
            </button>
          </div>
        ),
      },
    ],
    [t, copiedId]
  );

  return (
    <div className="container mx-auto px-4 py-8 text-primary shadow-2xl m-4">
      <h1 className="text-3xl font-bold text-center mb-6">
        {t("ticketing.title")}
      </h1>

      <DataGrid
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage={t("No events available")}
        className="shadow-none"
      />
    </div>
  );
}

function formatDate(dateString) {
  const options = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
}
